import { useState, type FormEvent } from "react";
import type { JSX } from "react";

import { ApiRequestError } from "../../common/api/apiClient.js";
import { PrimaryButton } from "../../common/components/PrimaryButton.js";
import { StatusScreen } from "../../common/components/StatusScreen.js";
import { useCreateTenant, useOnboardingStatus } from "../query/onboardingQueries.js";

/**
 * Schritt 3 bis 7 des Onboardings (Issue #30): Registrierung und Bestätigung der
 * Mailadresse erledigt Keycloak, hier entsteht der eigene Workspace.
 */
export function OnboardingPage(): JSX.Element {
  const status = useOnboardingStatus(true);
  const createTenant = useCreateTenant();
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const fieldErrors = createTenant.error instanceof ApiRequestError ? createTenant.error.details : {};

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!status.data?.canCreateTenant || status.isError || createTenant.isPending) return;
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setFormError("Bitte den Namen des Unternehmens angeben.");
      return;
    }
    setFormError(null);
    const body = domain.trim().length > 0 ? { name: trimmed, domain: domain.trim() } : { name: trimmed };
    createTenant.mutate(body);
  };

  if (status.isPending) {
    return <StatusScreen title="Zugang wird geprüft …" />;
  }

  if (status.isError) {
    return <StatusScreen title="Zugang konnte nicht geprüft werden" description={status.error.message}
      action={<PrimaryButton onClick={() => void status.refetch()}>Erneut versuchen</PrimaryButton>} />;
  }

  if (!status.data.canCreateTenant || fieldErrors.reason === "invitation_required") {
    return (
      <div className="mx-auto mt-16 max-w-md rounded-2xl border border-line bg-surface-raised p-8 text-center">
        <h1 className="text-lg font-semibold text-ink">Einladung erforderlich</h1>
        <p className="mt-2 text-sm text-ink-muted">
          In dieser Installation gibt es bereits ein Unternehmen. Bitte einen Administrator um
          eine Einladung.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-16 max-w-md px-4">
      <h1 className="text-xl font-semibold text-ink">Unternehmen anlegen</h1>
      <p className="mt-2 text-sm text-ink-muted">
        Der erste Schritt in buma. Du wirst Eigentümer dieses Workspaces und kannst danach
        Kolleginnen und Kollegen einladen.
      </p>

      <form onSubmit={submit} noValidate className="mt-6 space-y-4">
        <div>
          <label htmlFor="tenant-name" className="block text-sm font-medium text-ink">
            Name des Unternehmens
          </label>
          <input
            id="tenant-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="organization"
            required
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
          />
          {fieldErrors.name ? <p className="mt-1 text-sm text-danger">{fieldErrors.name}</p> : null}
        </div>

        <div>
          <label htmlFor="tenant-domain" className="block text-sm font-medium text-ink">
            Domain <span className="font-normal text-ink-muted">(optional)</span>
          </label>
          <input
            id="tenant-domain"
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            placeholder="firma.de"
            inputMode="url"
            className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-ink"
          />
          <p className="mt-1 text-xs text-ink-muted">
            Wird zunächst nur vorgemerkt. Der Nachweis über DNS folgt in der Administration.
          </p>
          {fieldErrors.domain ? (
            <p className="mt-1 text-sm text-danger">{fieldErrors.domain}</p>
          ) : null}
        </div>

        {formError ? <p className="text-sm text-danger">{formError}</p> : null}
        {createTenant.error && Object.keys(fieldErrors).length === 0 ? (
          <p className="text-sm text-danger">{createTenant.error.message}</p>
        ) : null}

        <PrimaryButton
          type="submit"
          disabled={createTenant.isPending}
          title={createTenant.isPending ? "Das Unternehmen wird angelegt" : undefined}
        >
          {createTenant.isPending ? "Wird angelegt …" : "Unternehmen anlegen"}
        </PrimaryButton>
      </form>
    </div>
  );
}
